import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import ExportToExcelButton from "../components/ExportToExcelButton";
import DocumentDispatchDialog from "../components/DocumentDispatchDialog";
import FleetEquipmentAttached from "../components/FleetEquipmentAttached";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Loader2,
  Plus,
  Sparkles,
  FileText,
  DollarSign,
  Send,
  Download,
  Eye,
  ChevronLeft,
  CheckCircle,
  AlertTriangle,
  Clock,
  CreditCard,
  XCircle,
  Receipt,
  Settings,
  Zap,
  Bell,
  Upload,
  CheckSquare,
  Square,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Invoices = () => {
  const { isOfficeManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [completableQuotes, setCompletableQuotes] = useState([]);
  
  // Selected invoice for detail view
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDispatchOpen, setInvoiceDispatchOpen] = useState(false);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [createForm, setCreateForm] = useState({
    quote_id: "", due_date: "", notes: "", payment_terms: "Net 30",
    tax_rate: 0, discount_percent: 0, discount_amount: 0
  });
  
  const [paymentForm, setPaymentForm] = useState({
    amount: "", payment_date: new Date().toISOString().split("T")[0],
    payment_method: "e-transfer", reference: "", notes: ""
  });

  // Automation settings
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  // Phase 19.17: bulk-export selection state
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [invoiceBulkExporting, setInvoiceBulkExporting] = useState(false);
  const toggleSelectInvoice = (id) => {
    setSelectedInvoiceIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleSelectAllInvoices = () => {
    if (selectedInvoiceIds.length === invoices.length) setSelectedInvoiceIds([]);
    else setSelectedInvoiceIds(invoices.map((i) => i.id));
  };
  const handleBulkExportInvoices = async (format) => {
    if (selectedInvoiceIds.length === 0) return;
    setInvoiceBulkExporting(true);
    try {
      const response = await axios.post(
        `${API}/invoices/bulk-export`,
        { invoice_ids: selectedInvoiceIds, format },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.setAttribute("download", `invoices-export-${stamp}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${selectedInvoiceIds.length} invoices as ${format.toUpperCase()}`);
      setSelectedInvoiceIds([]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to bulk export");
    } finally {
      setInvoiceBulkExporting(false);
    }
  };
  const [automationSettings, setAutomationSettings] = useState({
    enabled: true,
    auto_create_on_complete: true,
    default_tax_rate: 0,
    default_payment_terms: "Net 30",
    require_approval: true,
    auto_send_reminders: true,
    reminder_days: [7, 14, 30]
  });

  const fetchAutomationSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/invoice-automation/settings`);
      setAutomationSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch automation settings", error);
    }
  }, []);

  const saveAutomationSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/invoice-automation/settings`, automationSettings);
      toast.success("Automation settings saved");
      setSettingsDialogOpen(false);
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const fetchInvoices = useCallback(async (status = null) => {
    try {
      const url = status && status !== "all" 
        ? `${API}/invoices?status=${status}`
        : `${API}/invoices`;
      const response = await axios.get(url);
      setInvoices(response.data);
    } catch (error) {
      console.error("Failed to fetch invoices", error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/invoices/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats", error);
    }
  }, []);

  const fetchCompletableQuotes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/invoices/completable-quotes`);
      setCompletableQuotes(response.data);
    } catch (error) {
      console.error("Failed to fetch completable quotes", error);
    }
  }, []);

  const fetchInvoiceDetail = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}`);
      setSelectedInvoice(response.data);
    } catch (error) {
      toast.error("Failed to load invoice details");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchInvoices(), fetchStats(), fetchCompletableQuotes(), fetchAutomationSettings()]);
      setLoading(false);
      
      // Check for quick action intent
      const quickAction = sessionStorage.getItem('quickAction');
      if (quickAction === 'create-invoice') {
        sessionStorage.removeItem('quickAction');
        setCreateDialogOpen(true);
      }
    };
    loadData();
  }, [fetchInvoices, fetchStats, fetchCompletableQuotes, fetchAutomationSettings]);

  useEffect(() => {
    if (activeTab !== "create") {
      fetchInvoices(activeTab);
    }
  }, [activeTab, fetchInvoices]);

  const handleCreateInvoice = async () => {
    if (!createForm.quote_id) {
      toast.error("Please select a quote");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...createForm,
        tax_rate: parseFloat(createForm.tax_rate) || 0,
        discount_percent: parseFloat(createForm.discount_percent) || 0,
        discount_amount: parseFloat(createForm.discount_amount) || 0
      };
      const response = await axios.post(`${API}/invoices`, payload);
      toast.success(`Invoice ${response.data.invoice_number} created`);
      setCreateDialogOpen(false);
      setCreateForm({
        quote_id: "", due_date: "", notes: "", payment_terms: "Net 30",
        tax_rate: 0, discount_percent: 0, discount_amount: 0
      });
      fetchInvoices();
      fetchStats();
      fetchCompletableQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/send`);
      toast.success("Invoice sent");
      if (selectedInvoice) {
        fetchInvoiceDetail(invoiceId);
      }
      fetchInvoices(activeTab);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invoice");
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        invoice_id: selectedInvoice.id,
        amount: parseFloat(paymentForm.amount),
        payment_date: paymentForm.payment_date,
        payment_method: paymentForm.payment_method,
        reference: paymentForm.reference,
        notes: paymentForm.notes
      };
      await axios.post(`${API}/invoices/${selectedInvoice.id}/payments`, payload);
      toast.success("Payment recorded");
      setPaymentDialogOpen(false);
      setPaymentForm({
        amount: "", payment_date: new Date().toISOString().split("T")[0],
        payment_method: "e-transfer", reference: "", notes: ""
      });
      fetchInvoiceDetail(selectedInvoice.id);
      fetchInvoices(activeTab);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    try {
      await axios.patch(`${API}/invoices/${invoiceId}/status?status=${newStatus}`);
      toast.success(`Invoice status updated to ${newStatus}`);
      if (selectedInvoice) {
        fetchInvoiceDetail(invoiceId);
      }
      fetchInvoices(activeTab);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleSendReminders = async () => {
    try {
      const response = await axios.post(`${API}/invoices/send-overdue-reminders`);
      if (response.data.reminders?.length > 0) {
        toast.success(`Sent ${response.data.reminders.length} reminder(s)`);
        fetchInvoices(activeTab);
        fetchStats();
      } else {
        toast.info("No overdue invoices need reminders");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminders");
    }
  };

  const handleCancelInvoice = async (invoiceId) => {
    if (!window.confirm("Are you sure you want to cancel this invoice?")) return;
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`);
      toast.success("Invoice cancelled");
      setSelectedInvoice(null);
      fetchInvoices(activeTab);
      fetchStats();
      fetchCompletableQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel invoice");
    }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const formatCurrency = (amount) => `$${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const getStatusBadge = (status) => {
    const variants = {
      draft: { color: "bg-gray-500", icon: FileText },
      sent: { color: "bg-blue-500", icon: Send },
      paid: { color: "bg-emerald-500", icon: CheckCircle },
      overdue: { color: "bg-red-500", icon: AlertTriangle },
      cancelled: { color: "bg-gray-400", icon: XCircle }
    };
    const v = variants[status] || variants.draft;
    const Icon = v.icon;
    return (
      <Badge className={`${v.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="invoices-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Invoice Detail View
  if (selectedInvoice) {
    const balanceDue = selectedInvoice.total_amount - (selectedInvoice.paid_amount || 0);
    
    return (
      <div className="space-y-6 animate-in" data-testid="invoice-detail">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setSelectedInvoice(null)}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Invoices
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <Receipt className="h-8 w-8 text-primary" />
              Invoice {selectedInvoice.invoice_number}
            </h1>
            <div className="flex items-center gap-3 mt-2">
              <Select
                value={selectedInvoice.status}
                onValueChange={(newStatus) => handleUpdateStatus(selectedInvoice.id, newStatus)}
              >
                <SelectTrigger className="w-[140px] h-8" data-testid="invoice-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-muted-foreground">
                Issued: {selectedInvoice.issue_date} | Due: {selectedInvoice.due_date}
              </span>
            </div>
          </div>
          {isOfficeManager && (
            <div className="flex gap-2">
              <Button
                onClick={() => setInvoiceDispatchOpen(true)}
                data-testid="dispatch-invoice-btn"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Send className="h-4 w-4 mr-2" />
                Send &amp; File
              </Button>
              <DocumentDispatchDialog kind="invoice" docId={selectedInvoice.id} open={invoiceDispatchOpen} onOpenChange={setInvoiceDispatchOpen} />
              {selectedInvoice.status === "draft" && (
                <Button onClick={() => handleSendInvoice(selectedInvoice.id)}>
                  <Send className="h-4 w-4 mr-2" />
                  Send Invoice
                </Button>
              )}
              {selectedInvoice.status !== "paid" && selectedInvoice.status !== "cancelled" && (
                <Button onClick={() => {
                  setPaymentForm({...paymentForm, amount: balanceDue.toFixed(2)});
                  setPaymentDialogOpen(true);
                }}>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Record Payment
                </Button>
              )}
              <Button variant="outline" onClick={() => handleDownloadPdf(selectedInvoice.id, selectedInvoice.invoice_number)}>
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              {selectedInvoice.status !== "paid" && (
                <Button variant="destructive" onClick={() => handleCancelInvoice(selectedInvoice.id)}>
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Subtotal</p>
              <p className="text-2xl font-bold">{formatCurrency(selectedInvoice.subtotal)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Tax ({selectedInvoice.tax_rate}%)</p>
              <p className="text-2xl font-bold">{formatCurrency(selectedInvoice.tax_amount)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Total Amount</p>
              <p className="text-2xl font-bold text-primary">{formatCurrency(selectedInvoice.total_amount)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Balance Due</p>
              <p className={`text-2xl font-bold ${balanceDue > 0 ? "text-red-600" : "text-emerald-600"}`}>
                {formatCurrency(balanceDue)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Invoice Details */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Bill To</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="font-medium">{selectedInvoice.company_name}</p>
              {selectedInvoice.company_address && <p className="text-sm text-muted-foreground">{selectedInvoice.company_address}</p>}
              {selectedInvoice.contact_name && <p className="text-sm">Attn: {selectedInvoice.contact_name}</p>}
              {selectedInvoice.contact_email && <p className="text-sm text-muted-foreground">{selectedInvoice.contact_email}</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Invoice Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">Property:</span>
                <span>{selectedInvoice.property_address}</span>
                <span className="text-muted-foreground">Payment Terms:</span>
                <span>{selectedInvoice.payment_terms}</span>
                {selectedInvoice.sent_date && (
                  <>
                    <span className="text-muted-foreground">Sent Date:</span>
                    <span>{selectedInvoice.sent_date}</span>
                  </>
                )}
                {selectedInvoice.paid_date && (
                  <>
                    <span className="text-muted-foreground">Paid Date:</span>
                    <span>{selectedInvoice.paid_date}</span>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Fleet & Equipment (iter140) */}
        {(selectedInvoice.fleet_ids?.length > 0 || selectedInvoice.equipment_ids?.length > 0) && (
          <FleetEquipmentAttached
            fleetIds={selectedInvoice.fleet_ids || []}
            equipmentIds={selectedInvoice.equipment_ids || []}
          />
        )}

        {/* Line Items */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Line Items</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedInvoice.line_items?.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{item.description}</TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.total)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2">
                  <TableCell colSpan={3} className="text-right font-medium">Subtotal</TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(selectedInvoice.subtotal)}</TableCell>
                </TableRow>
                {selectedInvoice.discount_amount > 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-right">Discount ({selectedInvoice.discount_percent}%)</TableCell>
                    <TableCell className="text-right text-red-600">-{formatCurrency(selectedInvoice.discount_amount)}</TableCell>
                  </TableRow>
                )}
                {selectedInvoice.tax_amount > 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-right">Tax ({selectedInvoice.tax_rate}%)</TableCell>
                    <TableCell className="text-right">{formatCurrency(selectedInvoice.tax_amount)}</TableCell>
                  </TableRow>
                )}
                <TableRow className="border-t-2 bg-muted/50">
                  <TableCell colSpan={3} className="text-right font-bold text-lg">Total</TableCell>
                  <TableCell className="text-right font-bold text-lg">{formatCurrency(selectedInvoice.total_amount)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Payment History */}
        {selectedInvoice.payments?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Payment History</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedInvoice.payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>{payment.payment_date}</TableCell>
                      <TableCell className="capitalize">{payment.payment_method.replace("-", " ")}</TableCell>
                      <TableCell>{payment.reference || "-"}</TableCell>
                      <TableCell className="text-right text-emerald-600 font-medium">{formatCurrency(payment.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        {selectedInvoice.notes && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{selectedInvoice.notes}</p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <Receipt className="h-8 w-8 text-primary" />
            Invoices
          </h1>
          <p className="text-muted-foreground mt-1">
            Generate and manage invoices from completed jobs
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isOfficeManager && (
            <Link to="/invoices/upload" data-testid="upload-invoice-link">
              <Button variant="outline">
                <Upload className="h-4 w-4 mr-2" />
                Upload Invoice
              </Button>
            </Link>
          )}
          {isOfficeManager && (
            <Link to="/bulk-upload?kind=invoice" data-testid="bulk-upload-invoice-link">
              <Button variant="outline">
                <Sparkles className="h-4 w-4 mr-2" />
                Mass Upload
              </Button>
            </Link>
          )}
          {isOfficeManager && (
            <Button variant="outline" onClick={() => setSettingsDialogOpen(true)} data-testid="invoice-settings-btn">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
          )}
          {isOfficeManager && stats?.by_status?.overdue > 0 && (
            <Button variant="outline" onClick={handleSendReminders} className="border-orange-500 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950/30">
              <Bell className="h-4 w-4 mr-2" />
              Send Reminders ({stats.by_status.overdue})
            </Button>
          )}
          {isOfficeManager && (
            <ExportToExcelButton path="invoices" label="Excel" />
          )}
          {isOfficeManager && completableQuotes.length > 0 && (
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Invoice
            </Button>
          )}
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedInvoiceIds.length > 0 && (
        <Card className="border-primary/30 bg-primary/5" data-testid="invoice-bulk-action-bar">
          <CardContent className="py-3 px-4 flex flex-wrap items-center gap-3">
            <span className="font-medium text-sm">
              {selectedInvoiceIds.length} selected
            </span>
            <div className="flex-1" />
            <Button variant="outline" size="sm" onClick={() => setSelectedInvoiceIds([])} data-testid="invoice-clear-selection-btn">
              Clear
            </Button>
            <Button size="sm" onClick={() => handleBulkExportInvoices("pdf")} disabled={invoiceBulkExporting} data-testid="invoice-bulk-export-pdf-btn">
              {invoiceBulkExporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Export PDFs (ZIP)
            </Button>
            <Button size="sm" variant="secondary" onClick={() => handleBulkExportInvoices("docx")} disabled={invoiceBulkExporting} data-testid="invoice-bulk-export-docx-btn">
              <Download className="h-4 w-4 mr-2" />
              Export DOCX (ZIP)
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Automation Status Card */}
      {isOfficeManager && automationSettings && (
        <Card className={`border-2 ${automationSettings.auto_create_on_complete ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/30' : 'border-gray-200'}`}>
          <CardContent className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Zap className={`h-5 w-5 ${automationSettings.auto_create_on_complete ? 'text-emerald-600' : 'text-gray-400'}`} />
              <div>
                <p className="font-medium text-sm">
                  Auto-Invoice: {automationSettings.auto_create_on_complete ? 'Enabled' : 'Disabled'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {automationSettings.auto_create_on_complete 
                    ? 'Draft invoices are created automatically when jobs are completed' 
                    : 'Manual invoice creation required'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              {automationSettings.auto_create_on_complete && (
                <>
                  <span>Tax: {automationSettings.default_tax_rate}%</span>
                  <span>Terms: {automationSettings.default_payment_terms}</span>
                  {automationSettings.auto_send_reminders && (
                    <Badge variant="outline" className="gap-1">
                      <Bell className="h-3 w-3" />
                      Reminders On
                    </Badge>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <FileText className="h-4 w-4" />
                <span className="text-sm">Total Invoiced</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(stats.total_invoiced)}</p>
              <p className="text-xs text-muted-foreground">{stats.total_invoices} invoices</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">Paid</span>
              </div>
              <p className="text-2xl font-bold text-emerald-600">{formatCurrency(stats.total_paid)}</p>
              <p className="text-xs text-muted-foreground">{stats.by_status?.paid || 0} paid</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span className="text-sm">Outstanding</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(stats.total_outstanding)}</p>
              <p className="text-xs text-muted-foreground">{stats.by_status?.sent || 0} pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm">Overdue</span>
              </div>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(stats.overdue_amount)}</p>
              <p className="text-xs text-muted-foreground">{stats.by_status?.overdue || 0} overdue</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          <TabsTrigger value="draft" data-testid="tab-draft">Draft</TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent">Sent</TabsTrigger>
          <TabsTrigger value="overdue" data-testid="tab-overdue">Overdue</TabsTrigger>
          <TabsTrigger value="paid" data-testid="tab-paid">Paid</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {invoices.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <button
                      type="button"
                      onClick={toggleSelectAllInvoices}
                      data-testid="invoice-select-all-btn"
                      aria-label="Select all invoices"
                    >
                      {selectedInvoiceIds.length === invoices.length && invoices.length > 0 ? (
                        <CheckSquare className="h-4 w-4 text-primary" />
                      ) : (
                        <Square className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Issue Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => {
                  const isSelected = selectedInvoiceIds.includes(invoice.id);
                  return (
                  <TableRow key={invoice.id} className={`cursor-pointer hover:bg-muted/50 ${isSelected ? "bg-primary/5" : ""}`} onClick={() => fetchInvoiceDetail(invoice.id)}>
                    <TableCell onClick={(e) => { e.stopPropagation(); toggleSelectInvoice(invoice.id); }}>
                      <button
                        type="button"
                        data-testid={`select-invoice-${invoice.id}`}
                        aria-label={isSelected ? "Deselect invoice" : "Select invoice"}
                      >
                        {isSelected ? (
                          <CheckSquare className="h-4 w-4 text-primary" />
                        ) : (
                          <Square className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>
                    </TableCell>
                    <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                    <TableCell>{invoice.company_name}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{invoice.property_address}</TableCell>
                    <TableCell>{invoice.issue_date}</TableCell>
                    <TableCell>{invoice.due_date}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(invoice.total)}</TableCell>
                    <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" onClick={() => fetchInvoiceDetail(invoice.id)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        {/* iter141 Beta P2 fix: quick Record Payment action on
                            the list row (previously users could only reach it
                            from the detail view). Opens the same dialog. */}
                        {isOfficeManager &&
                          invoice.status !== "paid" &&
                          invoice.status !== "cancelled" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`row-record-payment-${invoice.id}`}
                            title="Record Payment"
                            onClick={async () => {
                              await fetchInvoiceDetail(invoice.id);
                              setPaymentForm((prev) => ({ ...prev, amount: invoice.balance_due }));
                              setPaymentDialogOpen(true);
                            }}
                          >
                            <DollarSign className="h-4 w-4 text-emerald-600" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(invoice.id, invoice.invoice_number)}>
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No invoices found</p>
                {isOfficeManager && completableQuotes.length > 0 && (
                  <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create First Invoice
                  </Button>
                )}
                {completableQuotes.length === 0 && (
                  <p className="text-sm mt-2">Complete a job to create an invoice</p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Invoice Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
            <DialogDescription>Generate an invoice from a completed job</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Completed Quote *</Label>
              <Select value={createForm.quote_id} onValueChange={(v) => setCreateForm({...createForm, quote_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a completed quote" />
                </SelectTrigger>
                <SelectContent>
                  {completableQuotes.map((quote) => (
                    <SelectItem key={quote.id} value={quote.id}>
                      {quote.quote_number} - {quote.company_name} ({formatCurrency(quote.total_amount)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input type="date" value={createForm.due_date} onChange={(e) => setCreateForm({...createForm, due_date: e.target.value})} />
                <p className="text-xs text-muted-foreground">Default: 30 days</p>
              </div>
              <div className="space-y-2">
                <Label>Payment Terms</Label>
                <Select value={createForm.payment_terms} onValueChange={(v) => setCreateForm({...createForm, payment_terms: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                    <SelectItem value="Net 15">Net 15</SelectItem>
                    <SelectItem value="Net 30">Net 30</SelectItem>
                    <SelectItem value="Net 45">Net 45</SelectItem>
                    <SelectItem value="Net 60">Net 60</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tax Rate (%)</Label>
                <Input type="number" step="0.1" value={createForm.tax_rate} onChange={(e) => setCreateForm({...createForm, tax_rate: e.target.value})} placeholder="e.g., 5 for GST" />
              </div>
              <div className="space-y-2">
                <Label>Discount (%)</Label>
                <Input type="number" step="0.1" value={createForm.discount_percent} onChange={(e) => setCreateForm({...createForm, discount_percent: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={createForm.notes} onChange={(e) => setCreateForm({...createForm, notes: e.target.value})} rows={2} placeholder="Additional notes for the invoice" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateInvoice} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>Record a payment for invoice {selectedInvoice?.invoice_number}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Payment Date *</Label>
                <Input type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({...paymentForm, payment_method: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="e-transfer">E-Transfer</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reference #</Label>
                <Input value={paymentForm.reference} onChange={(e) => setPaymentForm({...paymentForm, reference: e.target.value})} placeholder="Cheque # or Transaction ID" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={paymentForm.notes} onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Automation Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Invoice Automation Settings
            </DialogTitle>
            <DialogDescription>
              Configure automatic invoice generation and reminders
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Auto-Create Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Auto-Create Invoices</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically create draft invoices when jobs are completed
                  </p>
                </div>
                <Switch
                  checked={automationSettings.auto_create_on_complete}
                  onCheckedChange={(checked) => setAutomationSettings({...automationSettings, auto_create_on_complete: checked})}
                />
              </div>
              
              {automationSettings.auto_create_on_complete && (
                <div className="pl-4 border-l-2 border-primary/20 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Default Tax Rate (%)</Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        value={automationSettings.default_tax_rate} 
                        onChange={(e) => setAutomationSettings({...automationSettings, default_tax_rate: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Default Payment Terms</Label>
                      <Select 
                        value={automationSettings.default_payment_terms} 
                        onValueChange={(v) => setAutomationSettings({...automationSettings, default_payment_terms: v})}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                          <SelectItem value="Net 15">Net 15</SelectItem>
                          <SelectItem value="Net 30">Net 30</SelectItem>
                          <SelectItem value="Net 45">Net 45</SelectItem>
                          <SelectItem value="Net 60">Net 60</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Require Approval</Label>
                      <p className="text-xs text-muted-foreground">
                        Require manual approval before sending auto-generated invoices
                      </p>
                    </div>
                    <Switch
                      checked={automationSettings.require_approval}
                      onCheckedChange={(checked) => setAutomationSettings({...automationSettings, require_approval: checked})}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Reminders Section */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Overdue Reminders</Label>
                  <p className="text-sm text-muted-foreground">
                    Send automatic email reminders for overdue invoices
                  </p>
                </div>
                <Switch
                  checked={automationSettings.auto_send_reminders}
                  onCheckedChange={(checked) => setAutomationSettings({...automationSettings, auto_send_reminders: checked})}
                />
              </div>
              
              {automationSettings.auto_send_reminders && (
                <div className="pl-4 border-l-2 border-primary/20">
                  <Label className="text-sm">Reminder Schedule (days past due)</Label>
                  <div className="flex gap-2 mt-2">
                    {[7, 14, 30].map((day) => (
                      <Badge 
                        key={day}
                        variant={automationSettings.reminder_days?.includes(day) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => {
                          const days = automationSettings.reminder_days || [];
                          const newDays = days.includes(day) 
                            ? days.filter(d => d !== day)
                            : [...days, day].sort((a,b) => a-b);
                          setAutomationSettings({...automationSettings, reminder_days: newDays});
                        }}
                      >
                        {day} days
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Click to toggle. Reminders will be sent on selected days after due date.
                  </p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveAutomationSettings} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
